import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Skeleton,
    Stack,
    Typography,
} from '@mui/material';
import {
    CameraAltOutlined as CameraIcon,
    CheckCircle as CheckIcon,
    LinkOffOutlined as DisconnectIcon,
    OpenInNewOutlined as OpenIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
    useInstagramStatus,
    useInstagramDisconnect,
    useInstagramOAuthFlow,
} from '@/hooks/queries/instagram';
import { dialogOpen } from '@/modules/MuiDialog';
import type { InstagramAccountType } from '@/types/instagram';
import { ACCENT, ACCENT_HOVER, ACCENT_TINT, ACCENT_TINT_STRONG, PageHeader } from './_shared';

const ACCOUNT_TYPE_LABEL: Record<InstagramAccountType, string> = {
    BUSINESS: '비즈니스',
    MEDIA_CREATOR: '크리에이터',
    PERSONAL: '개인',
};

const TIPS = [
    { num: 1, body: <><b>Instagram Business 계정</b>이어야 합니다 (개인 계정 X)</> },
    { num: 2, body: <>자동 DM 은 <b>마지막 상호작용 후 24시간 내</b>에만 발송 가능</> },
    { num: 3, body: <>시간당 <b>최대 200건</b> 발송 (Meta 정책)</> },
];

const InstagramAccountPage = () => {
    const statusQuery = useInstagramStatus({ withProfile: true });
    const oauth = useInstagramOAuthFlow();
    const disconnect = useInstagramDisconnect();

    const handleDisconnect = () => {
        dialogOpen({
            message: 'Instagram 연결을 해제할까요?<br/>연결 해제 시 모든 자동화가 일시정지됩니다.',
            confirmText: '연결 해제',
            closeText: '취소',
            isAllowHtml: true,
            onConfirmed: async () => {
                try {
                    await disconnect.mutateAsync();
                    toast.success('Instagram 연결을 해제했어요');
                } catch (err: any) {
                    toast.error(err?.data?.message || 'Instagram 연결 해제에 실패했어요');
                }
            },
        });
    };

    const renderConnectedCard = () => {
        const data = statusQuery.data;
        if (!data) return null;
        const accountType = data.accountType ? ACCOUNT_TYPE_LABEL[data.accountType] : null;
        const isBusinessCapable = data.accountType === 'BUSINESS' || data.accountType === 'MEDIA_CREATOR';

        return (
            <Card variant="outlined" sx={{ borderRadius: 3, mb: 2.5 }}>
                <CardContent>
                    <Stack direction="row" spacing={2.5} alignItems="center">
                        <Avatar
                            src={data.profilePictureUrl || undefined}
                            alt={data.instagramUsername || ''}
                            sx={{ width: 64, height: 64, bgcolor: ACCENT_TINT_STRONG }}
                        >
                            <CameraIcon sx={{ color: ACCENT }} />
                        </Avatar>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 800 }} noWrap>
                                    @{data.instagramUsername}
                                </Typography>
                                <Chip
                                    size="small"
                                    icon={<CheckIcon sx={{ fontSize: 14 }} />}
                                    label="연결됨"
                                    sx={{
                                        bgcolor: 'rgba(76,175,80,0.12)',
                                        color: '#2e7d32',
                                        fontWeight: 700,
                                        '& .MuiChip-icon': { color: '#2e7d32' },
                                    }}
                                />
                                {accountType && (
                                    <Chip
                                        size="small"
                                        label={accountType}
                                        variant="outlined"
                                        sx={{ fontWeight: 600 }}
                                    />
                                )}
                            </Stack>
                            {data.name && (
                                <Typography variant="body2" sx={{ color: 'text.primary', mb: 0.5 }}>
                                    {data.name}
                                </Typography>
                            )}
                            <Stack direction="row" spacing={2.5} sx={{ color: 'text.secondary' }}>
                                {typeof data.followersCount === 'number' && (
                                    <Typography variant="caption">
                                        팔로워 <b>{data.followersCount.toLocaleString()}</b>
                                    </Typography>
                                )}
                                {typeof data.followsCount === 'number' && (
                                    <Typography variant="caption">
                                        팔로잉 <b>{data.followsCount.toLocaleString()}</b>
                                    </Typography>
                                )}
                                {typeof data.mediaCount === 'number' && (
                                    <Typography variant="caption">
                                        게시물 <b>{data.mediaCount.toLocaleString()}</b>
                                    </Typography>
                                )}
                            </Stack>
                        </Box>
                        <Button
                            onClick={handleDisconnect}
                            disabled={disconnect.isPending}
                            startIcon={<DisconnectIcon />}
                            variant="outlined"
                            color="error"
                            sx={{ textTransform: 'none', borderRadius: 2, cursor: 'pointer', flexShrink: 0 }}
                        >
                            연결 해제
                        </Button>
                    </Stack>

                    {data.biography && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 2, pt: 2, borderTop: '1px dashed', borderColor: 'divider', whiteSpace: 'pre-wrap' }}
                        >
                            {data.biography}
                        </Typography>
                    )}

                    {!isBusinessCapable && data.accountType === 'PERSONAL' && (
                        <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
                            현재 개인 계정은 자동 DM 발송이 불가능합니다. Business / Creator 계정으로 전환 후 다시
                            연결해주세요.
                        </Alert>
                    )}
                </CardContent>
            </Card>
        );
    };

    const renderConnectCard = () => (
        <Card
            variant="outlined"
            sx={{
                borderRadius: 3,
                mb: 2.5,
                background: 'linear-gradient(135deg,#fff8f2 0%,#ffe9d9 100%)',
                borderColor: '#ffd2b8',
            }}
        >
            <CardContent
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2.5,
                    py: 3,
                    flexWrap: { xs: 'wrap', md: 'nowrap' },
                }}
            >
                <Box
                    sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 3,
                        bgcolor: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <CameraIcon sx={{ color: ACCENT, fontSize: 32 }} />
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.25 }}>
                        먼저 Instagram 계정을 연결하세요
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        팝업으로 Meta 로그인을 진행합니다. 약 1분 안에 완료돼요.
                    </Typography>
                </Box>
                <Button
                    onClick={oauth.start}
                    disabled={oauth.isBusy}
                    variant="contained"
                    startIcon={oauth.isBusy ? <CircularProgress size={16} color="inherit" /> : <OpenIcon />}
                    disableElevation
                    sx={{
                        bgcolor: ACCENT,
                        color: '#fff',
                        fontWeight: 700,
                        textTransform: 'none',
                        borderRadius: 2,
                        px: 2.5,
                        py: 1.25,
                        cursor: 'pointer',
                        transition: 'transform 120ms ease, background-color 120ms ease',
                        willChange: 'transform',
                        '&:hover': { bgcolor: ACCENT_HOVER, transform: 'translateY(-1px)' },
                        '&:active': { transform: 'translateY(0)' },
                        '&.Mui-disabled': { bgcolor: ACCENT, opacity: 0.6, color: '#fff' },
                    }}
                >
                    {oauth.isPolling ? '연결 확인 중…' : 'Instagram 연결하기'}
                </Button>
            </CardContent>
        </Card>
    );

    const renderTips = () => (
        <Card variant="outlined" sx={{ borderRadius: 3 }}>
            <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                    연결 전에 알아두세요
                </Typography>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    divider={<Box sx={{ borderLeft: { md: '1px dashed' }, borderColor: { md: 'divider' } }} />}
                >
                    {TIPS.map((tip) => (
                        <Stack key={tip.num} direction="row" spacing={1.5} alignItems="flex-start" sx={{ flex: 1 }}>
                            <Box
                                sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    bgcolor: ACCENT_TINT,
                                    color: ACCENT,
                                    fontWeight: 700,
                                    fontSize: 12,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                {tip.num}
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                {tip.body}
                            </Typography>
                        </Stack>
                    ))}
                </Stack>
            </CardContent>
        </Card>
    );

    return (
        <Box>
            <PageHeader title="인스타 연결" desc="Instagram Business 계정을 연결·관리합니다" />

            {statusQuery.isLoading ? (
                <Card variant="outlined" sx={{ borderRadius: 3, mb: 2.5 }}>
                    <CardContent>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Skeleton variant="circular" width={64} height={64} />
                            <Box sx={{ flexGrow: 1 }}>
                                <Skeleton width={160} height={24} />
                                <Skeleton width={240} height={18} />
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>
            ) : statusQuery.isError ? (
                <Alert severity="error" sx={{ borderRadius: 2, mb: 2.5 }}>
                    Instagram 연결 상태를 불러오지 못했어요.
                </Alert>
            ) : statusQuery.data?.isConnected ? (
                renderConnectedCard()
            ) : (
                renderConnectCard()
            )}

            {!statusQuery.data?.isConnected && renderTips()}
        </Box>
    );
};

export default InstagramAccountPage;
